import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { usersApi } from '../../api/users';
import { GQLArgs, ErrorForToast } from '../../types';
import { AxiosError } from 'axios';

type UpdateUserResponse = { updateUser: any };

export const useMutationUpdateUser = (args?: GQLArgs<UpdateUserResponse>) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<UpdateUserResponse | undefined>();

  const mutation = useCallback(
    async ({ variables }: { variables: any }) => {
      setLoading(true);
      try {
        // Look up user id by email first, then update
        const listRes = await usersApi.list({ search: variables.email, per_page: 1 });
        const userId = listRes.data.nodes[0]?.id;
        if (!userId) throw new Error('User not found');

        const res = await usersApi.update(userId, {
          full_name: variables.fullName,
          email: variables.email,
          active: variables.active,
          speciality: variables.speciality,
          ref_id: variables.refId,
          role: variables.role,
        });
        const shaped: UpdateUserResponse = { updateUser: res.data };
        setData(shaped);
        args?.onCompleted?.(shaped);
        toast.success('User has been updated', { autoClose: 5000 });
        return shaped;
      } catch (err: any) {
        const axiosError = err as AxiosError<{
          code?: string;
          message?: string;
          extra?: Record<string, any>;
        }>;
        const status = axiosError.response?.status;
        const body = axiosError.response?.data;

        if (status === 422 && body?.extra?.details) {
          const details = body.extra.details;
          const errorMessages: ErrorForToast[] = Object.keys(details).map((key) => ({
            name: key,
            errors: Array.isArray(details[key]) ? details[key] : [details[key]],
          }));
          args?.onUIError?.(errorMessages);
        }

        args?.onError?.(err);
      } finally {
        setLoading(false);
      }
    },
    [args],
  );

  return { mutation, data, loading, error: undefined };
};
